<script setup>
import {computed, nextTick, ref, watch} from "vue";
import {useBillingStore} from "@/stores/billing";
import {useI18n} from "vue-i18n";
import {useToast} from "vue-toastification";
import CountryDropdown from "@/components/CountryDropdown.vue";
import AppIcon from "@/components/AppIcon.vue";

const emits = defineEmits(['saved'])

const billing = useBillingStore()
const i18n = useI18n()
const toast = useToast()

const form = ref(null)
const formEmail = ref(null)
const mailForm = ref(null)
const mailInput = ref(null)
const draft = ref(billing.emptyAddress())
const mode = ref(null)
const isSaving = ref(false)

// Die E-Mail, nach der die Fertig-Karte fragt, solange keine brauchbare
// hinterlegt ist. Bringt die Buchung eine mit, steht sie schon da — dann
// bleibt nur ein Tipp auf „Speichern“.
const mailDraft = ref(billing.prefill.email)

const stored = computed(() => billing.address)

// Ein Backend ohne `complete` zählt auch einen Teilstand als erledigt
// (hasAddress). Fehlt ihm die E-Mail, lässt sie sich nicht allein nachtragen:
// der Gäste-Endpunkt nimmt nur eine vollständige Anschrift an. Dann geht es
// nur über das Formular — die Fertig-Karte zeigt statt des E-Mail-Felds den
// Weg dorthin. Vollständig heißt dabei dasselbe wie IsComplete im Backend
// (billing.isComplete).
const emailNeedsForm = computed(() => billing.hasAddress && !billing.isComplete && !billing.hasEmail)

// Drei Zustände. Ein offenes Formular gewinnt gegen alles — sonst ließe sich
// eine einmal gespeicherte Anschrift nie mehr ändern. `done` heißt: die
// Anschrift taugt für eine Rechnung; ein Teilstand aus dem Backoffice zählt
// dafür nicht (nur ein Backend ohne `complete` zählt ihn mit, siehe
// emailNeedsForm).
//
// Sonst `choice`: die Rechnung auf den eigenen Namen oder die Firma ist ein
// Angebot, keine Pflicht (Entscheidung Rida, 07.10.2026). Wer keine braucht,
// tut hier nichts — es wird nichts gespeichert, und in sevDesk entsteht nicht
// für jeden Gast ein eigener Kunde. Deshalb übernimmt der Block auch nichts
// mehr von allein aus dem Meldeschein; er belegt nur das Formular damit vor.
const state = computed(() => {
  if (mode.value) {
    return 'form'
  }

  if (billing.hasAddress) {
    return 'done'
  }

  return 'choice'
})

const isCompany = computed(() => draft.value.type === 'company')

// Was der Ein-Tipp-Weg speichern würde, steht auf dem Knopf. Sonst bestätigt
// der Gast eine Anschrift, die er nicht gesehen hat.
const guestAddressPreview = computed(() => {
  if (!billing.canUseGuestAddress) {
    return ''
  }

  const p = billing.prefill

  return [p.street, [p.postalCode, p.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')
})

const storedLines = computed(() => {
  const a = stored.value
  if (!a) {
    return []
  }

  const person = [a.firstName, a.lastName].filter(Boolean).join(' ')
  const lines = []

  if (a.type === 'company' && a.companyName) {
    lines.push(a.companyName)
    if (person) {
      lines.push(i18n.t('billing.attn', {name: person}))
    }
  } else if (person) {
    lines.push(person)
  }

  if (a.street) {
    lines.push(a.street)
  }

  const cityLine = [a.postalCode, a.city].filter(Boolean).join(' ')
  if (cityLine) {
    lines.push(cityLine)
  }

  return lines
})

function openForm(type) {
  const prefill = {...billing.prefill}

  // Eine Firmenanschrift ist nicht die Wohnanschrift des Gastes. Name und
  // E-Mail dürfen vorbelegt sein — Straße, PLZ und Ort nicht, sonst steht
  // die Privatadresse auf der Firmenrechnung, weil sie schon im Feld stand.
  if (type === 'company') {
    prefill.street = ''
    prefill.postalCode = ''
    prefill.city = ''
    prefill.country = ''
  }

  // Eine vollständige Anschrift gilt so, wie sie gespeichert ist. Ein
  // Teilstand aus dem Backoffice ergänzt dagegen nur die Vorbelegung — seine
  // leeren Felder sollen dort nichts löschen.
  const kept = billing.isComplete ? {...stored.value} : billing.filledFields(stored.value)

  draft.value = {
    ...prefill,
    ...kept,
    // Pflichtfeld: fehlt die E-Mail im Gespeicherten, steht die aus der
    // Buchung da, sofern sie taugt.
    email: kept.email || prefill.email,
    type,
  }

  // Beim Wechsel auf "privat" bleibt kein Firmenname stehen, den der Gast
  // gerade abgewählt hat.
  if (type === 'private') {
    draft.value.companyName = ''
    draft.value.vatId = ''
  }

  mode.value = type
}

// Wer privat abrechnet, hat seine Anschrift im Meldeschein schon eingetippt:
// das Formular ist damit vorbelegt (openForm), samt der E-Mail aus der
// Buchung, sofern sie taugt. Gespeichert wird erst, wenn der Gast es
// bestätigt — mit E-Mail, damit die Rechnung ihn auch erreicht.
function usePrivate() {
  openForm('private')
}

// Eine Weiterleitungsadresse des Portals nimmt der Server zwar an, für die
// Rechnung taugt sie aber nicht. Besser gleich beim Speichern sagen, warum —
// statt dieselbe Frage danach noch einmal zu stellen.
//
// Auch bei jeder Eingabe: im E-Mail-Feld der Fertig-Karte prüft der Browser
// vor dem submit-Event selbst — stünde die Meldung dort noch, ließe er den
// Gast nie mehr speichern. Beim Speichern zusätzlich für den Fall, dass die
// Adresse schon vorbelegt war.
function rejectRelay(input) {
  const email = input.value.trim()

  input.setCustomValidity(
      billing.isRelayEmail(email) ? i18n.t('billing.emailRelayHint', {email}) : ''
  )
}

// minlength prüft der Browser nur an Werten, die der Gast selbst getippt hat.
// Eine Anschrift aus dem Backoffice (dort gilt keine Mindestlänge, ein „J“ als
// Vorname geht durch) stünde vorbelegt ungeprüft im Formular — und der
// Gäste-Endpunkt lehnte sie ab. Deshalb dieselbe Grenze als eigene Meldung,
// gezählt in Zeichen wie im Backend.
function checkLength(input) {
  if (!(input.minLength > 0)) {
    return
  }

  const length = [...input.value].length

  input.setCustomValidity(
      length > 0 && length < input.minLength ? i18n.t('billing.tooShort', {min: input.minLength}) : ''
  )
}

// Prüft das offene Formular so streng wie der Server, auch die Felder, die
// der Gast nicht angefasst hat, und zeigt an, was nicht passt.
function validateForm() {
  rejectRelay(formEmail.value)
  form.value.querySelectorAll('input[minlength]').forEach(checkLength)

  return form.value.reportValidity()
}

function submit() {
  if (!validateForm()) {
    return
  }

  isSaving.value = true

  billing
      .save(draft.value, 'form')
      .then(() => {
        mode.value = null
        emits('saved')
      })
      .catch(err => {
        console.error(err)
        toast.error(i18n.t('alerts.somethingWentWrong'))
      })
      .finally(() => isSaving.value = false)
}

function submitEmail() {
  rejectRelay(mailInput.value)

  if (!mailForm.value.checkValidity()) {
    mailForm.value.reportValidity()

    return
  }

  const email = mailDraft.value.trim()

  isSaving.value = true

  billing
      .saveEmail(email)
      .then(() => {
        emits('saved')
      })
      .catch(err => {
        console.error(err)

        // Vollständig heißt nur: kein Pflichtfeld ist leer. Der Gäste-
        // Endpunkt verlangt mehr (Vor- und Nachname, Straße und Ort mit
        // mindestens zwei Zeichen), das Backoffice speichert ohne diese
        // Grenze. Lehnt er ab, geht es nur im ganzen Formular weiter: die
        // E-Mail steht dort schon, die Prüfung zeigt, welches Feld nicht
        // passt.
        if (err.response?.status === 400) {
          edit()
          draft.value.email = email
          nextTick(() => {
            // Findet die Prüfung an der Anschrift nichts, kann es nur die
            // E-Mail gewesen sein — der Server prüft sie strenger, als jedes
            // Muster im Browser es nachbilden kann. Dann das Feld markieren,
            // statt die Anschrift zu beschuldigen.
            if (validateForm()) {
              formEmail.value.setCustomValidity(i18n.t('billing.checkEmail'))
              formEmail.value.reportValidity()
              toast.warning(i18n.t('billing.checkEmail'))

              return
            }

            toast.warning(i18n.t('billing.checkAddress'))
          })

          return
        }

        toast.error(i18n.t('alerts.somethingWentWrong'))
      })
      .finally(() => isSaving.value = false)
}

function edit() {
  openForm(stored.value?.type ?? 'private')
}

function cancel() {
  mode.value = null
}

// Wechselt der Gast im offenen Formular den Typ, muss auch die Pflichtfeld-
// Logik umschalten — sonst blockiert ein required-Feld, das nicht mehr da ist.
watch(() => draft.value.type, (type, previous) => {
  if (type !== 'private') {
    return
  }

  draft.value.companyName = ''
  draft.value.vatId = ''

  // Zurück auf "privat" heißt: wieder die eigene Anschrift. Die Felder tragen
  // in diesem Moment noch die Firmenadresse — die stehen zu lassen wäre die
  // schlechteste aller Vorbelegungen. Leere Felder werden ebenfalls gefüllt,
  // damit "privat" nie ein leeres Formular bedeutet.
  const p = billing.prefill
  const fromCompany = previous === 'company'

  for (const field of ['street', 'postalCode', 'city', 'country']) {
    if (fromCompany || !draft.value[field]) {
      draft.value[field] = p[field]
    }
  }

  for (const field of ['firstName', 'lastName']) {
    if (!draft.value[field]) {
      draft.value[field] = p[field]
    }
  }
})
</script>

<template>
  <section class="billing">
    <h2 class="ww-eyebrow billing__label">{{ $t('billing.label') }}</h2>

    <!-- Rechnung nur auf Wunsch: zwei große Ziele, kein Formular. Wer keine
         braucht, tut hier nichts. -->
    <div v-if="state === 'choice'" class="ww-card billing__card">
      <div class="billing__head">
        <span class="billing__plate">
          <app-icon name="receipt_long" :size="21"/>
        </span>
        <div>
          <h3 class="billing__title">{{ $t('billing.question') }}</h3>
          <p class="billing__hint">{{ $t('billing.questionHint') }}</p>
        </div>
      </div>

      <div class="billing__choices">
        <button
            type="button"
            class="billing__choice"
            :disabled="isSaving"
            @click="usePrivate"
        >
          <app-icon name="person" :size="22"/>
          <span class="billing__choice-title">{{ $t('billing.choicePrivate') }}</span>
          <span class="billing__choice-sub">
            {{ guestAddressPreview || $t('billing.choicePrivateSub') }}
          </span>
        </button>

        <button
            type="button"
            class="billing__choice"
            :disabled="isSaving"
            @click="openForm('company')"
        >
          <app-icon name="apartment" :size="22"/>
          <span class="billing__choice-title">{{ $t('billing.choiceCompany') }}</span>
          <span class="billing__choice-sub">{{ $t('billing.choiceCompanySub') }}</span>
        </button>
      </div>

      <p class="billing__legal">{{ $t('billing.legal') }}</p>
    </div>

    <!-- Anschrift steht. Fehlt noch eine E-Mail-Adresse, die für die Rechnung
         taugt, fragt die Karte direkt danach — das ganze Formular wäre dafür
         zu viel. -->
    <div v-else-if="state === 'done'" class="ww-card ww-card--success billing__done">
      <div class="billing__done-row">
        <span class="billing__done-mark">
          <app-icon name="check" :size="19"/>
        </span>
        <div class="billing__done-body">
          <div class="billing__done-title">
            {{ $t('billing.savedTitle') }}
          </div>
          <address class="billing__address">
            <span v-for="(line, i) in storedLines" :key="'bl-' + i">{{ line }}<br/></span>
          </address>
          <div v-if="billing.hasEmail" class="billing__done-mail">
            {{ $t('billing.sendTo') }}: {{ stored.email }}
          </div>
        </div>
        <button type="button" class="billing__change" @click="edit">
          <app-icon name="edit" :size="17"/>
          {{ $t('change') }}
        </button>
      </div>

      <form
          v-if="!billing.hasEmail && !emailNeedsForm"
          action="#"
          @submit.prevent="submitEmail"
          ref="mailForm"
          class="billing__mail"
      >
        <label class="ww-field" for="billing-mail">
          <span class="billing__mail-question">{{ $t('billing.emailQuestion') }}</span>
          <input
              type="email"
              class="ww-input"
              id="billing-mail"
              ref="mailInput"
              :placeholder="$t('billing.emailPlaceholderPrivate')"
              inputmode="email"
              autocomplete="email"
              :pattern="billing.emailPattern"
              v-model="mailDraft"
              @input="rejectRelay($event.target)"
              required
          />
          <!-- Es gibt nur die Weiterleitung des Portals: sagen, warum wir
               trotzdem fragen. -->
          <span v-if="billing.relayEmail" class="billing__field-hint">
            {{ $t('billing.emailRelayHint', {email: billing.relayEmail}) }}
          </span>
        </label>

        <button type="submit" class="ww-btn ww-btn--primary ww-btn--sm" :disabled="isSaving">
          <span v-if="isSaving" class="ww-spinner"></span>
          <app-icon v-else name="save" :size="19"/>
          {{ $t('save') }}
        </button>
      </form>

      <!-- Ein Teilstand, den nur ein älteres Backend als erledigt zählt
           (emailNeedsForm): die E-Mail allein nähme der Server nicht an.
           Statt des Felds der Weg ins Formular, falls der Gast es schon
           einmal geschlossen hat. -->
      <div v-else-if="emailNeedsForm" class="billing__mail">
        <div>
          <p class="billing__mail-question">{{ $t('billing.emailQuestion') }}</p>
          <p class="billing__field-hint">{{ $t('billing.emailNeedsAddress') }}</p>
        </div>

        <button type="button" class="ww-btn ww-btn--primary ww-btn--sm" @click="edit">
          <app-icon name="edit" :size="19"/>
          {{ $t('billing.completeAddress') }}
        </button>
      </div>

      <p class="billing__legal">{{ $t('billing.legal') }}</p>
    </div>

    <!-- Formular. novalidate: geprüft wird in submit() (validateForm), samt
         der eigenen Meldungen. Prüfte der Browser vorher selbst, hielte ihn
         eine veraltete eigene Meldung auf — etwa nachdem der Typwechsel ein
         Feld per Skript neu gefüllt hat —, und submit() liefe nie. Bei
         jeder Eingabe wird die Längenmeldung trotzdem gleich nachgeführt. -->
    <form
        v-else
        action="#"
        novalidate
        @submit.prevent="submit"
        @input="checkLength($event.target)"
        ref="form"
        class="ww-card billing__form"
    >
      <div class="billing__type" role="group" :aria-label="$t('billing.typeLabel')">
        <button
            type="button"
            class="billing__type-option"
            :class="{'billing__type-option--active': !isCompany}"
            :aria-pressed="!isCompany"
            @click="draft.type = 'private'"
        >
          {{ $t('billing.choicePrivate') }}
        </button>
        <button
            type="button"
            class="billing__type-option"
            :class="{'billing__type-option--active': isCompany}"
            :aria-pressed="isCompany"
            @click="draft.type = 'company'"
        >
          {{ $t('billing.choiceCompany') }}
        </button>
      </div>

      <template v-if="isCompany">
        <label class="ww-field" for="billing-company">
          <span class="ww-field__label">{{ $t('billing.companyName') }}</span>
          <input
              type="text"
              class="ww-input"
              id="billing-company"
              :placeholder="$t('billing.companyNamePlaceholder')"
              v-model="draft.companyName"
              required
          />
        </label>

        <label class="ww-field" for="billing-vat">
          <span class="ww-field__label">{{ $t('billing.vatId') }} <span class="billing__optional">{{ $t('billing.optional') }}</span></span>
          <input
              type="text"
              class="ww-input"
              id="billing-vat"
              placeholder="DE123456789"
              autocapitalize="characters"
              v-model="draft.vatId"
          />
        </label>
      </template>

      <div class="ww-grid-2">
        <label class="ww-field" for="billing-firstname">
          <span class="ww-field__label">
            {{ $t('firstName') }}
            <span v-if="isCompany" class="billing__optional">{{ $t('billing.optional') }}</span>
          </span>
          <input
              type="text"
              class="ww-input"
              id="billing-firstname"
              :placeholder="$t('firstName')"
              v-model="draft.firstName"
              :required="!isCompany"
              minlength="2"
          />
        </label>

        <label class="ww-field" for="billing-lastname">
          <span class="ww-field__label">
            {{ $t('lastName') }}
            <span v-if="isCompany" class="billing__optional">{{ $t('billing.optional') }}</span>
          </span>
          <input
              type="text"
              class="ww-input"
              id="billing-lastname"
              :placeholder="$t('lastName')"
              v-model="draft.lastName"
              :required="!isCompany"
              minlength="2"
          />
        </label>
      </div>

      <label class="ww-field" for="billing-street">
        <span class="ww-field__label">{{ $t('address') }}</span>
        <input
            type="text"
            class="ww-input"
            id="billing-street"
            :placeholder="$t('address')"
            v-model="draft.street"
            required
            minlength="2"
        />
      </label>

      <div class="billing__zip-city">
        <label class="ww-field" for="billing-zip">
          <span class="ww-field__label">{{ $t('postalCode') }}</span>
          <input
              type="text"
              class="ww-input"
              id="billing-zip"
              :placeholder="$t('postalCode')"
              v-model="draft.postalCode"
              required
          />
        </label>

        <label class="ww-field" for="billing-city">
          <span class="ww-field__label">{{ $t('city') }}</span>
          <input
              type="text"
              class="ww-input"
              id="billing-city"
              :placeholder="$t('city')"
              v-model="draft.city"
              required
              minlength="2"
          />
        </label>
      </div>

      <country-dropdown
          title="country"
          placeholder="select"
          id="billing-country"
          v-model="draft.country"
          :required="true"
      />

      <label class="ww-field" for="billing-email">
        <span class="ww-field__label">{{ $t('billing.emailLabel') }}</span>
        <input
            type="email"
            class="ww-input"
            id="billing-email"
            ref="formEmail"
            :placeholder="isCompany ? $t('billing.emailPlaceholder') : $t('billing.emailPlaceholderPrivate')"
            inputmode="email"
            autocomplete="email"
            :pattern="billing.emailPattern"
            v-model="draft.email"
            @input="rejectRelay($event.target)"
            required
        />
        <span class="billing__field-hint">{{ $t('billing.emailHint') }}</span>
      </label>

      <div class="billing__actions">
        <button type="submit" class="ww-btn ww-btn--primary ww-btn--sm" :disabled="isSaving">
          <span v-if="isSaving" class="ww-spinner"></span>
          <app-icon v-else name="save" :size="19"/>
          {{ $t('save') }}
        </button>
        <button
            type="button"
            class="ww-btn ww-btn--ghost ww-btn--sm"
            :disabled="isSaving"
            @click="cancel"
        >
          {{ $t('billing.cancel') }}
        </button>
      </div>

      <p class="billing__legal">{{ $t('billing.legal') }}</p>
    </form>
  </section>
</template>

<style scoped>
.billing__label {
  margin-top: 26px;
}

.billing__card {
  margin-top: 12px;
}

.billing__head {
  display: flex;
  align-items: flex-start;
  gap: 13px;
}

.billing__plate {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: var(--ww-r-xs);
  background: var(--ww-accent-chip);
  color: var(--ww-accent-icon);
  flex: 0 0 auto;
}

.billing__title {
  font-size: 17px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--ww-text);
}

.billing__hint {
  margin-top: 5px;
  font-size: 13.5px;
  line-height: 1.5;
  color: var(--ww-text-muted);
}

.billing__choices {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 16px;
}

.billing__choice {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 14px 13px;
  border: 1px solid var(--ww-border-input);
  border-radius: var(--ww-r-sm);
  background: var(--ww-surface);
  color: var(--ww-primary);
  text-align: left;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.billing__choice:hover:not(:disabled) {
  border-color: var(--ww-primary);
  background: var(--ww-primary-tint);
}

.billing__choice:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.billing__choice-title {
  margin-top: 8px;
  font-size: 15px;
  font-weight: 700;
  color: var(--ww-text);
}

.billing__choice-sub {
  font-size: 12.5px;
  line-height: 1.4;
  color: var(--ww-text-muted);
}

.billing__legal {
  margin-top: 13px;
  font-size: 12px;
  line-height: 1.5;
  color: var(--ww-text-faint);
}

.billing__done {
  margin-top: 12px;
}

.billing__done-row {
  display: flex;
  align-items: flex-start;
  gap: 13px;
}

.billing__done-mark {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--ww-success);
  color: #fff;
  flex: 0 0 auto;
}

.billing__done-body {
  flex: 1 1 auto;
  min-width: 0;
}

.billing__done-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--ww-success-ink);
}

.billing__address {
  margin-top: 5px;
  font-size: 14px;
  font-style: normal;
  line-height: 1.5;
  color: var(--ww-success-muted);
  overflow-wrap: anywhere;
}

.billing__done-mail {
  margin-top: 6px;
  font-size: 12.5px;
  color: var(--ww-success-muted);
  overflow-wrap: anywhere;
}

/* Weiß in der grünen Karte: die Anschrift ist erledigt, das hier noch nicht. */
.billing__mail {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
  padding: 16px 14px;
  border: 1px solid var(--ww-success-border);
  border-radius: var(--ww-r-md);
  background: var(--ww-surface);
}

.billing__mail-question {
  display: block;
  margin-bottom: 9px;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.35;
  color: var(--ww-text);
}

.billing__done .billing__legal {
  color: var(--ww-success-muted);
}

.billing__form .billing__legal {
  margin-top: 0;
}

.billing__change {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex: 0 0 auto;
  padding: 7px 11px;
  border: 1px solid var(--ww-success-border);
  border-radius: var(--ww-r-pill);
  background: var(--ww-surface);
  font-size: 12.5px;
  font-weight: 700;
  color: var(--ww-success-ink);
  white-space: nowrap;
}

.billing__form {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 12px;
}

.billing__type {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.billing__type-option {
  border: 1px solid var(--ww-border-input);
  border-radius: var(--ww-r-sm);
  padding: 12px 8px;
  background: var(--ww-surface);
  color: var(--ww-text-body);
  font-size: 14px;
  font-weight: 700;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.billing__type-option--active {
  border-color: var(--ww-primary);
  background: var(--ww-primary);
  color: #fff;
}

.billing__zip-city {
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: 12px;
}

.billing__optional {
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: none;
  color: var(--ww-text-disabled);
}

.billing__field-hint {
  display: block;
  margin-top: 6px;
  font-size: 12.5px;
  line-height: 1.45;
  color: var(--ww-text-muted);
  overflow-wrap: anywhere;
}

.billing__actions {
  display: flex;
  gap: 10px;
  margin-top: 2px;
}

@media (max-width: 359px) {
  .billing__choices {
    grid-template-columns: 1fr;
  }
}
</style>
